import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {formatDate as formatApiDate} from '../utils/dateTime';
import {useNavigate, useSearchParams} from 'react-router-dom';
import createAxiosInstance, {toQueryParams} from "../support/AxiosSupport";
import {hasPermission, useWardrobe} from "../context/WardrobeContext";
import {PURPOSE_OPTIONS} from "../support/itemLifecycle";
import {useToast} from "../context/ToastContext";
import {pluralItems, pluralTimes} from "../utils/plural";
import {WARDROBE_SESSION_KEY, WARDROBE_SESSION_TTL_MS} from "../support/wardrobeSession";
import {
    buildCatalogParams,
    DEFAULT_SORT_BY,
    DEFAULT_SORT_DIR,
    emptyListFilters,
    LIST_FILTER_KEYS,
    ListFilterKey,
    ListFilters,
    parseCatalogParams,
} from "../support/catalogFilters";
import Spinner from "../components/Spinner";
import PageSizeSelect from "../components/PageSizeSelect";
import AddClothingItemModal from "../components/AddClothingItemModal";
import AddAiRecognitionModal from "../components/AddAiRecognitionModal";
import BulkAddToListsModal from "../components/BulkAddToListsModal";
import OneInOneOutDialog from "../components/OneInOneOutDialog";
import {isOneInOneOutEnabled} from "../service/preferencesCache";
import AuthImage from "../components/AuthImage";
import IconButton from "../components/IconButton";
import Dialog from "../components/Dialog";
import CatalogFilters, {EMPTY_FILTER_REFS, FilterRefs, RefOption} from "../components/CatalogFilters";
import ClothingItemCard, {CatalogItem} from "../components/ClothingItemCard";
import {
    AlertTriangleIcon,
    CheckCheckIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ClockIcon,
    FilterIcon,
    GridIcon,
    ListIcon,
    PlusIcon,
    RefreshIcon,
    SearchIcon,
    ShirtIcon,
    SparklesIcon,
    TableIcon,
    TrashIcon,
    XIcon,
} from "../components/icons";
import {useDocumentTitle} from '../hooks/useDocumentTitle';

const FILTERS_STORAGE_KEY = 'clothes-wardrobe-filters';
const PAGE_SIZE_STORAGE_KEY = 'clothes-wardrobe-pagesize';
// Сайдбар фильтров на десктопе сворачивается: при 1200 px постоянная колонка 240 px
// отнимала у сетки целую карточку (C3). По умолчанию свёрнут — каталог начинается с вещей.
const SIDEBAR_STORAGE_KEY = 'clothes-wardrobe-filters-collapsed';
// Снапшот каталога для возврата с карточки (QA-7): ключ и TTL общие с support/wardrobeSession.
const SESSION_STATE_KEY = WARDROBE_SESSION_KEY;
const SESSION_TTL_MS = WARDROBE_SESSION_TTL_MS;
const PAGE_SIZE_OPTIONS = [20, 40, 60, 100];

/** Сетка карточек — один класс на скелетоны и на список, чтобы они не разъезжались. */
const GRID_CLASS = 'grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4';

/** Сколько категорий показываем быстрыми чипами над списком. */
const QUICK_CATEGORIES = 8;

const SORT_OPTIONS = [
    { label: 'По дате добавления', sortBy: 'createdAt' },
    { label: 'По AI-рейтингу',     sortBy: 'aiRating' },
    { label: 'По частоте ношения', sortBy: 'totalWears' },
    { label: 'По дате ношения',    sortBy: 'lastWornAt' },
    { label: 'По состоянию',       sortBy: 'condition.sortOrder' },
    { label: 'Избранные',          sortBy: 'favorite' },
    { label: 'По названию',        sortBy: 'name' },
    { label: 'По дате покупки',    sortBy: 'purchaseDate' },
];

interface PageResponse {
    content: CatalogItem[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
}

interface WardrobeStats {
    total: number;
    /**
     * Не носили дольше notWornDaysThreshold дней — порог стратегии, как в аналитике.
     * `null`, когда истории ещё недостаточно: тогда чип «Давно не носили» не рисуем.
     */
    notWornRecently: number | null;
    notWornDaysThreshold?: number;
    inTrash: number;
    noPhoto: number;
}

type SessionState = {
    items: CatalogItem[];
    page: number;
    totalPages: number;
    totalElements: number;
    pageSize: number;
    search: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
    hasDefects: boolean;
    waterProof: boolean | null;
    // Deep-link фильтры аналитики (analytics-deeplinks-bg-disposal, FR-2)
    notWornDays?: number | null;
    attention?: boolean;
    /** «Только избранные» (CTHS-273). */
    favorite?: boolean;
    ownerKey: string;
    scrollY: number;
    timestamp: number;
} & ListFilters;

/** Колонки табличного вида. `sortBy` — только ключи из белого списка бэкенда. */
const TABLE_COLUMNS: { label: string; sortBy?: string }[] = [
    { label: 'Фото' },
    { label: 'Название', sortBy: 'name' },
    { label: 'Категория' },
    { label: 'Бренд' },
    { label: 'Размер' },
    { label: 'Носки', sortBy: 'totalWears' },
    { label: 'Последняя носка', sortBy: 'lastWornAt' },
    { label: 'Рейтинг', sortBy: 'aiRating' },
    { label: 'Состояние', sortBy: 'condition.sortOrder' },
    { label: 'Дата покупки', sortBy: 'purchaseDate' },
];

/** По названию естественный порядок — от А; по числам и датам — сначала большее и свежее. */
const ASCENDING_BY_DEFAULT = new Set(['name']);

// Старый ключ сортировки `rating` (скрытый Elo) заменён видимым AI-рейтингом —
// нормализуем сохранённое в localStorage значение при чтении.
const LEGACY_SORT_MAPPING: Record<string, string> = { rating: 'aiRating' };

function normalizeSortBy(value: unknown): string | null {
    if (typeof value !== 'string' || !value) return null;
    const mapped = LEGACY_SORT_MAPPING[value] ?? value;
    return SORT_OPTIONS.some(o => o.sortBy === mapped) ? mapped : null;
}

// ── Skeleton loading states (styleguide: loading → skeleton для data regions) ──
const ItemCardSkeleton: React.FC = () => (
    <div data-testid="item-card-skeleton" className="card overflow-hidden flex flex-col animate-pulse">
        <div className="aspect-[3/4] rounded bg-ink-100 dark:bg-ink-800"/>
        <div className="p-3 space-y-2">
            <div className="h-3.5 w-3/4 rounded bg-ink-100 dark:bg-ink-800"/>
            <div className="h-3 w-1/2 rounded bg-ink-100 dark:bg-ink-800"/>
        </div>
    </div>
);

const TableRowSkeleton: React.FC = () => (
    <tr data-testid="table-row-skeleton" className="animate-pulse">
        {TABLE_COLUMNS.map(col => (
            <td key={col.label} className="px-4 py-3"><div className="h-3.5 rounded bg-ink-100 dark:bg-ink-800"/></td>
        ))}
    </tr>
);

function readPageSize(): number {
    try {
        const raw = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        if (raw) {
            const v = parseInt(raw, 10);
            if (PAGE_SIZE_OPTIONS.includes(v)) return v;
        }
    } catch (_) { /* ignore */ }
    return 20;
}

function readSidebarCollapsed(): boolean {
    try {
        return localStorage.getItem(SIDEBAR_STORAGE_KEY) !== 'false';
    } catch (_) {
        return true;
    }
}

const ClothingList: React.FC = () => {
    const navigate = useNavigate();
    const {showToast} = useToast();
    const {selectedOwnerId, isOwnWardrobe, permissions} = useWardrobe();
    const canAdd = isOwnWardrobe || hasPermission(permissions, 'ADD_ITEMS');
    const canMoveToTrash = isOwnWardrobe || hasPermission(permissions, 'MOVE_TO_TRASH');
    const canManageListItems = isOwnWardrobe || hasPermission(permissions, 'MANAGE_LIST_ITEMS');
    const canEdit = isOwnWardrobe || hasPermission(permissions, 'EDIT_ITEMS');
    const canBulk = canManageListItems || canMoveToTrash;

    const [items, setItems] = useState<CatalogItem[]>([]);
    const [loading, setLoading] = useState(true);
    // Явный error-state списка (styleguide: loading / empty / error различаются).
    const [loadError, setLoadError] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    // Мобильный доступ к фильтрам: bottom sheet на ширинах < lg (сайдбар скрыт).
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
    // Сортировка на мобиле — тоже шторка: в ряду из трёх контролов селекту места нет (C12).
    const [mobileSortOpen, setMobileSortOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(readSidebarCollapsed);
    const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [pageSize, setPageSize] = useState<number>(readPageSize);
    const [modalOpen, setModalOpen] = useState(false);
    const [aiModalOpen, setAiModalOpen] = useState(false);
    const [bulkAddModalOpen, setBulkAddModalOpen] = useState(false);
    const [addMenuOpen, setAddMenuOpen] = useState(false);
    // «Одно пришло — одно ушло»: только что созданная вещь для диалога кандидатов на выбытие
    const [oneInOneOutItem, setOneInOneOutItem] = useState<{id: string; name: string} | null>(null);

    const maybePromptOneInOneOut = useCallback(async (item: {id: string; name?: string}) => {
        if (await isOneInOneOutEnabled()) {
            setOneInOneOutItem({id: item.id, name: item.name ?? 'Новая вещь'});
        }
    }, []);

    // Search
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    // Фильтры-множества — один объект вместо тринадцати состояний: так отбор целиком
    // уходит в URL, в localStorage и в снапшот сессии без ручного перечисления.
    const [lists, setLists] = useState<ListFilters>(emptyListFilters);
    const [hasDefects, setHasDefects] = useState(false);
    const [waterProof, setWaterProof] = useState<boolean | null>(null);
    // Deep-link фильтры аналитики (FR-2): живут только в URL + state, в localStorage не пишутся.
    const [notWornDays, setNotWornDays] = useState<number | null>(null);
    const [attention, setAttention] = useState(false);
    // «Только избранные» (CTHS-273): двухпозиционный фильтр — включён либо не задан.
    const [favorite, setFavorite] = useState(false);
    const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>(DEFAULT_SORT_DIR);
    const [searchParams, setSearchParams] = useSearchParams();

    const [refs, setRefs] = useState<FilterRefs>(EMPTY_FILTER_REFS);

    // Selection
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    // Режим выделения включается явно кнопкой «Выбрать» или долгим нажатием (C6):
    // постоянные чекбоксы на каждом фото делали каталог похожим на файловый менеджер.
    const [selectionMode, setSelectionMode] = useState(false);
    // Меню ⋮ на карточке (U10): открыто для одной вещи; клик по кнопке не проваливается в ссылку.
    const [cardMenuId, setCardMenuId] = useState<string | null>(null);

    // Stats (QA-9): пока агрегаты перезапрашиваются — skeleton, а не старое число.
    const [stats, setStats] = useState<WardrobeStats | null>(null);
    const [statsLoading, setStatsLoading] = useState(true);

    // Init flags
    const [filtersLoaded, setFiltersLoaded] = useState(false);
    const skipNextRefreshRef = useRef(false);

    const abortRef = useRef<AbortController | null>(null);
    // FR-6: поколение запроса — ответы устаревших refreshItems/loadMore отбрасываются.
    const generationRef = useRef(0);
    const storageKey = selectedOwnerId ? `${FILTERS_STORAGE_KEY}-${selectedOwnerId}` : FILTERS_STORAGE_KEY;
    const ownerKey = selectedOwnerId ?? '__own__';

    // ── Search debounce ──────────────────────────────────────────────────────

    useEffect(() => {
        const timer = setTimeout(() => setSearch(searchInput), 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    // ── Session persistence ──────────────────────────────────────────────────

    const saveSession = useCallback((
        currentItems: CatalogItem[],
        currentPage: number,
        currentTotalPages: number,
        currentTotalElements: number,
    ) => {
        if (currentItems.length === 0) return;
        try {
            const state: SessionState = {
                items: currentItems,
                page: currentPage,
                totalPages: currentTotalPages,
                totalElements: currentTotalElements,
                pageSize,
                search,
                sortBy,
                sortDir,
                ...lists,
                hasDefects,
                waterProof,
                notWornDays,
                attention,
                favorite,
                ownerKey,
                scrollY: window.scrollY,
                timestamp: Date.now(),
            };
            sessionStorage.setItem(SESSION_STATE_KEY, JSON.stringify(state));
        } catch (_) { /* quota exceeded */ }
    }, [pageSize, search, sortBy, sortDir, lists, hasDefects, waterProof, notWornDays, attention, favorite, ownerKey]);

    // ── Восстановление отбора: localStorage, поверх него — URL ───────────────

    useEffect(() => {
        const restored = emptyListFilters();
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw) {
                const p = JSON.parse(raw);
                LIST_FILTER_KEYS.forEach(key => {
                    if (Array.isArray(p[key])) restored[key] = p[key];
                });
                if (p.hasDefects != null) setHasDefects(!!p.hasDefects);
                if (p.waterProof !== undefined) setWaterProof(p.waterProof);
                if (p.favorite != null) setFavorite(!!p.favorite);
                const savedSort = normalizeSortBy(p.sortBy);
                if (savedSort) setSortBy(savedSort);
                if (p.sortDir === 'asc' || p.sortDir === 'desc') setSortDir(p.sortDir);
            }
        } catch (e) {
            console.warn('Failed to load wardrobe filters', e);
        }
        // FR-2 (analytics-deeplinks-bg-disposal) и C13: query-параметры URL приоритетнее
        // localStorage — по ссылке открывается ровно тот отбор, который в ней записан.
        // Невалидные значения молча игнорируются; deep-link фильтры в localStorage не пишутся.
        try {
            const url = parseCatalogParams(searchParams);
            if (url.hasListFilters) {
                LIST_FILTER_KEYS.forEach(key => {
                    if (url.lists[key].length > 0) restored[key] = url.lists[key];
                });
            }
            if (url.hasDefects != null) setHasDefects(url.hasDefects);
            if (url.waterProof != null) setWaterProof(url.waterProof);
            if (url.notWornDays != null) setNotWornDays(url.notWornDays);
            if (url.attention) setAttention(true);
            if (url.favorite) setFavorite(true);
            if (url.search) { setSearchInput(url.search); setSearch(url.search); }
            const urlSort = normalizeSortBy(url.sortBy);
            if (urlSort) setSortBy(urlSort);
            if (url.sortDir) setSortDir(url.sortDir);
        } catch (e) {
            console.warn('Failed to apply URL filters', e);
        }
        setLists(restored);
        setFiltersLoaded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey]);

    // Save filters to localStorage
    useEffect(() => {
        if (!filtersLoaded) return;
        try {
            localStorage.setItem(storageKey, JSON.stringify({...lists, hasDefects, waterProof, favorite, sortBy, sortDir}));
        } catch (e) {
            console.warn('Failed to save wardrobe filters', e);
        }
    }, [filtersLoaded, storageKey, lists, hasDefects, waterProof, favorite, sortBy, sortDir]);

    // ── Зеркало отбора в URL (QA-7, C13) ─────────────────────────────────────
    // Весь отбор — поиск, сортировка, фильтры и тумблеры — живёт в query. Ссылка на
    // каталог воспроизводит ровно то, что видел пользователь: перезагрузка, «поделиться»
    // и возврат с карточки вещи попадают в те же результаты. Дефолты не пишем.
    useEffect(() => {
        if (!filtersLoaded) return;
        const next = buildCatalogParams({
            search, sortBy, sortDir, lists, hasDefects, waterProof, notWornDays, attention, favorite,
        });
        if (next.toString() !== searchParams.toString()) setSearchParams(next, {replace: true});
        // searchParams намеренно не в deps: эффект — источник записи, а не читатель.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersLoaded, search, sortBy, sortDir, lists, hasDefects, waterProof, notWornDays, attention, favorite]);

    // ── Restore session on mount ─────────────────────────────────────────────

    useEffect(() => {
        if (!filtersLoaded) return;
        try {
            const raw = sessionStorage.getItem(SESSION_STATE_KEY);
            if (raw) {
                const saved: SessionState = JSON.parse(raw);
                const fresh = Date.now() - saved.timestamp < SESSION_TTL_MS;
                const sameOwner = saved.ownerKey === ownerKey;
                const sameSearch = saved.search === search
                    && (saved.sortBy ?? DEFAULT_SORT_BY) === sortBy
                    && (saved.sortDir ?? DEFAULT_SORT_DIR) === sortDir;
                const sameFilters = LIST_FILTER_KEYS.every(key =>
                    JSON.stringify(saved[key] ?? []) === JSON.stringify(lists[key]))
                    && !!saved.hasDefects === hasDefects
                    && (saved.waterProof ?? null) === waterProof
                    && (saved.notWornDays ?? null) === notWornDays
                    && !!saved.attention === attention
                    && !!saved.favorite === favorite;
                if (fresh && sameOwner && sameSearch && sameFilters) {
                    skipNextRefreshRef.current = true;
                    setItems(saved.items);
                    setPage(saved.page);
                    setTotalPages(saved.totalPages);
                    setTotalElements(saved.totalElements);
                    setPageSize(saved.pageSize);
                    setLoading(false);
                    const scrollY = saved.scrollY;
                    requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, scrollY)));
                    return;
                }
            }
        } catch (_) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersLoaded]);

    // ── Data fetching ────────────────────────────────────────────────────────

    const buildParams = useCallback((targetPage: number) => {
        const params: Record<string, unknown> = {
            page: targetPage,
            size: pageSize,
            sortBy,
            sortDir,
            includeThrownAway: false,
        };
        if (selectedOwnerId) params.ownerId = selectedOwnerId;
        if (search) params.search = search;
        LIST_FILTER_KEYS.forEach(key => {
            if (lists[key].length > 0) params[key] = lists[key];
        });
        if (hasDefects) params.hasDefects = true;
        if (waterProof !== null) params.waterProof = waterProof;
        if (notWornDays !== null) params.notWornDays = notWornDays;
        if (attention) params.attention = true;
        if (favorite) params.favorite = true;
        return params;
    }, [pageSize, sortBy, sortDir, search, selectedOwnerId, lists, hasDefects, waterProof, notWornDays, attention, favorite]);

    const refreshItems = useCallback(async () => {
        abortRef.current?.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        const generation = ++generationRef.current;
        setLoading(true);
        setLoadError(false);
        setPage(0);
        setItems([]);
        try {
            const api = await createAxiosInstance();
            const response = await api.get<PageResponse>('/clothing/items', {
                params: toQueryParams(buildParams(0)),
                signal: ctrl.signal,
            });
            if (!ctrl.signal.aborted && generation === generationRef.current) {
                setItems(response.data.content);
                setTotalPages(response.data.totalPages);
                setTotalElements(response.data.totalElements);
            }
        } catch (error: unknown) {
            if ((error as {name?: string})?.name !== 'CanceledError') {
                console.error('Error fetching clothing items:', error);
                if (generation === generationRef.current) {
                    setItems([]);
                    // Не маскируем сбой под пустой гардероб — показываем error-state с «Повторить».
                    setLoadError(true);
                }
            }
        } finally {
            if (!ctrl.signal.aborted && generation === generationRef.current) setLoading(false);
        }
    }, [buildParams]);

    const loadMore = useCallback(async () => {
        const nextPage = page + 1;
        const generation = generationRef.current;
        setLoadingMore(true);
        try {
            const api = await createAxiosInstance();
            const response = await api.get<PageResponse>('/clothing/items', {
                params: toQueryParams(buildParams(nextPage)),
            });
            // FR-6: страница устаревшего поколения (после смены фильтра/сортировки/поиска) не аппендится.
            if (generation === generationRef.current) {
                setItems(prev => [...prev, ...response.data.content]);
                setPage(nextPage);
                setTotalPages(response.data.totalPages);
                setTotalElements(response.data.totalElements);
            }
        } catch (error) {
            console.error('Error loading more items:', error);
        } finally {
            setLoadingMore(false);
        }
    }, [page, buildParams]);

    // FR-5: автоподгрузка при прокрутке — sentinel под контентом (сетка и таблица)
    // + IntersectionObserver; кнопка «Показать ещё» остаётся фолбэком.
    const sentinelRef = useRef<HTMLDivElement | null>(null);
    const loadMoreRef = useRef(loadMore);
    useEffect(() => { loadMoreRef.current = loadMore; }, [loadMore]);

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || typeof IntersectionObserver === 'undefined') return;
        const canLoad = page + 1 < totalPages && !loadingMore && !loading;
        const observer = new IntersectionObserver(entries => {
            if (canLoad && entries.some(e => e.isIntersecting)) {
                loadMoreRef.current();
            }
        }, {rootMargin: '400px'});
        observer.observe(node);
        return () => observer.disconnect();
        // items.length — чтобы пере-подключиться, когда sentinel монтируется после загрузки.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, totalPages, loading, loadingMore, items.length]);

    // Load filter options
    const loadFilterOptions = useCallback(async () => {
        try {
            const api = await createAxiosInstance();
            const res = await api.get<Partial<Record<keyof FilterRefs, RefOption[]>>>(
                '/clothing/filter-options',
                { params: { includeThrownAway: false, ...(selectedOwnerId && { ownerId: selectedOwnerId }) } }
            );
            setRefs({
                categories: res.data?.categories ?? [],
                colors: res.data?.colors ?? [],
                sizes: res.data?.sizes ?? [],
                layers: res.data?.layers ?? [],
                seasons: res.data?.seasons ?? [],
                warmthLevels: res.data?.warmthLevels ?? [],
                materials: res.data?.materials ?? [],
                occasions: res.data?.occasions ?? [],
                styles: res.data?.styles ?? [],
                conditions: res.data?.conditions ?? [],
                weatherSuitabilities: res.data?.weatherSuitabilities ?? [],
                brands: res.data?.brands ?? [],
            });
        } catch (err) {
            console.error('Error loading filter options:', err);
        }
    }, [selectedOwnerId]);

    useEffect(() => { loadFilterOptions(); }, [loadFilterOptions]);

    // Load stats
    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<WardrobeStats>('/clothing/stats', {
                params: selectedOwnerId ? {ownerId: selectedOwnerId} : undefined,
            });
            setStats(res.data);
        } catch (err) {
            console.error('Error loading stats:', err);
        } finally {
            setStatsLoading(false);
        }
    }, [selectedOwnerId]);

    useEffect(() => { loadStats(); }, [loadStats]);

    /**
     * QA-9: после создания/правки/корзины/восстановления/списков обновляем не только
     * список, но и связанные агрегаты (счётчики фильтров и сигналы гардероба) — иначе
     * число вещей показывает устаревшее значение как актуальное.
     */
    const refreshAll = useCallback(() => {
        refreshItems();
        loadStats();
        loadFilterOptions();
    }, [refreshItems, loadStats, loadFilterOptions]);

    useDocumentTitle();

    useEffect(() => {
        if (!filtersLoaded) return;
        if (skipNextRefreshRef.current) { skipNextRefreshRef.current = false; return; }
        refreshItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersLoaded, refreshItems]);

    // Save session whenever items change
    useEffect(() => {
        if (!loading && items.length > 0) saveSession(items, page, totalPages, totalElements);
    }, [items, page, totalPages, totalElements, loading, saveSession]);

    // Sync selection when items change
    useEffect(() => {
        const selectable = new Set(items.filter(i => !i.thrownAway).map(i => i.id));
        setSelectedIds(prev => {
            const next = new Set<string>();
            prev.forEach(id => { if (selectable.has(id)) next.add(id); });
            return next;
        });
    }, [items]);

    // ── Helpers ──────────────────────────────────────────────────────────────

    const formatDate = (dateStr: string | null) => formatApiDate(dateStr);

    // Восстановление из корзины — общий «Отменить» для тостов карточки и массового действия.
    const restoreItems = useCallback(async (ids: string[]) => {
        try {
            const api = await createAxiosInstance();
            for (const id of ids) await api.post(`/clothing/items/${id}/restore`);
            showToast(ids.length === 1 ? 'Вещь возвращена в гардероб' : `Возвращено в гардероб: ${pluralItems(ids.length)}`, 'success');
        } catch (err) {
            console.error('Error restoring items:', err);
            showToast('Не удалось вернуть в гардероб', 'error');
        }
        refreshAll();
    }, [refreshAll, showToast]);

    const handleBulkMoveToTrash = useCallback(async () => {
        const ids = Array.from(selectedIds);
        if (ids.length === 0) return;
        try {
            const api = await createAxiosInstance();
            for (const id of ids) await api.post(`/clothing/items/${id}/trash`);
            setSelectedIds(new Set());
            showToast(
                ids.length === 1 ? 'Решение принято: вещь ждёт передачи' : `Решение принято: ${pluralItems(ids.length)}`,
                'success',
                {label: 'Отменить', onClick: () => { void restoreItems(ids); }},
            );
            refreshAll();
        } catch (err) {
            console.error('Error moving to trash:', err);
            showToast('Не удалось записать решение', 'error');
        }
    }, [selectedIds, refreshAll, restoreItems, showToast]);

    const handlePageSizeChange = (newSize: number) => {
        setPageSize(newSize);
        try { localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(newSize)); } catch (_) { /* ignore */ }
    };

    const selectableItems = items.filter(i => !i.thrownAway);
    const selectableIds = new Set(selectableItems.map(i => i.id));
    const allSelectableSelected = selectableItems.length > 0 && selectableItems.every(i => selectedIds.has(i.id));
    const toggleSelectAll = () => {
        if (allSelectableSelected) setSelectedIds(new Set());
        else setSelectedIds(new Set(selectableIds));
    };

    const toggleSelected = useCallback((itemId: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(itemId)) next.delete(itemId); else next.add(itemId);
            return next;
        });
    }, []);

    // Долгое нажатие на мобиле — второй вход в режим выделения, кроме кнопки «Выбрать».
    const handleLongPress = useCallback((itemId: string) => {
        if (!canBulk) return;
        setSelectionMode(true);
        setSelectedIds(prev => new Set(prev).add(itemId));
    }, [canBulk]);

    const exitSelection = useCallback(() => {
        setSelectedIds(new Set());
        setSelectionMode(false);
    }, []);

    const resetPagination = () => { setPage(0); setItems([]); };

    const toggleListFilter = useCallback((key: ListFilterKey, value: string) => {
        setLists(prev => ({
            ...prev,
            [key]: prev[key].includes(value) ? prev[key].filter(v => v !== value) : [...prev[key], value],
        }));
        setPage(0);
        setItems([]);
    }, []);

    const resetFilters = () => {
        setLists(emptyListFilters());
        setHasDefects(false);
        setWaterProof(null);
        setNotWornDays(null);
        setAttention(false);
        setFavorite(false);
        setSearchInput('');
        setSearch('');
        resetPagination();
    };

    const applySort = (nextSortBy: string, nextDir?: 'asc' | 'desc') => {
        setSortDir(nextDir ?? (nextSortBy === sortBy
            ? (sortDir === 'desc' ? 'asc' : 'desc')
            : (ASCENDING_BY_DEFAULT.has(nextSortBy) ? 'asc' : 'desc')));
        setSortBy(nextSortBy);
        resetPagination();
    };

    const handleItemClick = (e: React.MouseEvent, id: string) => {
        if (e.ctrlKey || e.metaKey || e.button === 1) return;
        e.preventDefault();
        navigate(`/clothing/${id}`);
    };

    const handleAuxClick = (e: React.MouseEvent, id: string) => {
        if (e.button === 1) window.open(`/clothing/${id}`, '_blank');
    };

    const handleToggleFavorite = useCallback(async (itemId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        try {
            const api = await createAxiosInstance();
            const res = await api.post<CatalogItem>(`/clothing/items/${itemId}/favorite`);
            setItems(prev => prev.map(item => item.id === itemId ? { ...item, favorite: res.data.favorite } : item));
        } catch (err) {
            console.error('Error toggling favorite:', err);
        }
    }, []);

    // ── Действия из меню ⋮ карточки ──────────────────────────────────────────

    const handleCardTrash = useCallback(async (itemId: string) => {
        setCardMenuId(null);
        try {
            const api = await createAxiosInstance();
            await api.post(`/clothing/items/${itemId}/trash`);
            showToast('Решение принято: вещь ждёт передачи', 'success', {label: 'Отменить', onClick: () => { void restoreItems([itemId]); }});
            refreshAll();
        } catch (err) {
            console.error('Error moving to trash:', err);
            showToast('Не удалось записать решение', 'error');
        }
    }, [refreshAll, restoreItems, showToast]);

    const handleCardWear = useCallback(async (itemId: string) => {
        setCardMenuId(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<Partial<CatalogItem>>(`/clothing/items/${itemId}/wear`);
            setItems(prev => prev.map(i => i.id === itemId
                ? {...i, totalWears: res.data?.totalWears ?? (i.totalWears ?? 0) + 1, lastWornAt: res.data?.lastWornAt ?? i.lastWornAt}
                : i));
            showToast('Носка записана', 'success');
        } catch (err) {
            console.error('Error recording wear:', err);
            showToast('Не удалось записать носку', 'error');
        }
    }, [showToast]);

    /** «Стирал» (CTHS-264): счётчик стирок обновляем из ответа, а не гадаем локально. */
    const handleCardWash = useCallback(async (itemId: string) => {
        setCardMenuId(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<Partial<CatalogItem>>(`/clothing/items/${itemId}/wash`);
            setItems(prev => prev.map(i => i.id === itemId
                ? {...i, totalWashes: res.data?.totalWashes ?? (i.totalWashes ?? 0) + 1}
                : i));
            showToast('Стирка отмечена', 'success');
        } catch (err) {
            console.error('Error recording wash:', err);
            showToast('Не удалось отметить стирку', 'error');
        }
    }, [showToast]);

    /**
     * Клик по бренду в карточке (CTHS-258) — отбор каталога по этому бренду: прежний выбор
     * брендов заменяется, иначе «показать всё Uniqlo» превращалось бы в накопление списка.
     * Ссылку `/clothing?brandIds=…` дописывает зеркало отбора в URL.
     */
    const handleBrandFilter = useCallback((brandId: string) => {
        setCardMenuId(null);
        setLists(prev => ({...prev, brandIds: [brandId]}));
        setPage(0);
        setItems([]);
        window.scrollTo({top: 0, behavior: 'smooth'});
    }, []);

    const handleCardAddToList = useCallback((itemId: string) => {
        setCardMenuId(null);
        setSelectedIds(new Set([itemId]));
        setBulkAddModalOpen(true);
    }, []);

    const handleCardNavigate = useCallback((itemId: string) => {
        setCardMenuId(null);
        navigate(`/clothing/${itemId}`);
    }, [navigate]);

    const handleToggleCardMenu = useCallback((itemId: string) => {
        setCardMenuId(prev => prev === itemId ? null : itemId);
    }, []);

    const closeCardMenu = useCallback(() => setCardMenuId(null), []);

    const hasMore = page + 1 < totalPages;
    const selectedCount = selectedIds.size;
    const activeFilterCount = LIST_FILTER_KEYS.reduce((sum, key) => sum + lists[key].length, 0)
        + (hasDefects ? 1 : 0) + (waterProof !== null ? 1 : 0)
        + (notWornDays !== null ? 1 : 0) + (attention ? 1 : 0) + (favorite ? 1 : 0);
    const hasActiveFilters = activeFilterCount > 0 || !!search;

    // Подпись значения фильтра: справочник для всего, кроме «Назначения» — там значения в коде.
    const refsByKey: Record<ListFilterKey, RefOption[]> = useMemo(() => ({
        categoryIds: refs.categories,
        purposes: [],
        colorIds: refs.colors,
        sizeIds: refs.sizes,
        seasonIds: refs.seasons,
        conditionIds: refs.conditions,
        materialIds: refs.materials,
        layerIds: refs.layers,
        occasionIds: refs.occasions,
        styleIds: refs.styles,
        weatherSuitabilityIds: refs.weatherSuitabilities,
        warmthLevelIds: refs.warmthLevels,
        brandIds: refs.brands,
    }), [refs]);

    const filterValueLabel = (key: ListFilterKey, value: string): string => {
        if (key === 'purposes') return PURPOSE_OPTIONS.find(o => o.value === value)?.label ?? value;
        return refsByKey[key].find(r => r.id === value)?.nameRu ?? value;
    };

    const activeChips: { key: string; label: string; remove: () => void }[] = [
        ...LIST_FILTER_KEYS.flatMap(key => lists[key].map(value => ({
            key: `${key}_${value}`,
            label: filterValueLabel(key, value),
            remove: () => toggleListFilter(key, value),
        }))),
        ...(favorite ? [{ key: 'favorite', label: 'Только избранные', remove: () => { setFavorite(false); resetPagination(); } }] : []),
        ...(hasDefects ? [{ key: 'defects', label: 'С дефектами', remove: () => { setHasDefects(false); resetPagination(); } }] : []),
        ...(waterProof ? [{ key: 'waterproof', label: 'Водостойкое', remove: () => { setWaterProof(null); resetPagination(); } }] : []),
        // FR-2: deep-link фильтры аналитики — видимые чипы со сбросом (state + URL)
        ...(notWornDays !== null ? [{ key: 'notWornDays', label: `Не носили > ${notWornDays} дн.`, remove: () => { setNotWornDays(null); resetPagination(); } }] : []),
        ...(attention ? [{ key: 'attention', label: 'Требуют внимания', remove: () => { setAttention(false); resetPagination(); } }] : []),
        ...(search ? [{ key: 'search', label: `"${search}"`, remove: () => { setSearchInput(''); setSearch(''); } }] : []),
    ];

    // Быстрые чипы категорий: активные всегда видны, остальные — первые QUICK_CATEGORIES.
    const quickCategories = useMemo(() => {
        const selected = refs.categories.filter(c => lists.categoryIds.includes(c.id));
        const rest = refs.categories.filter(c => !lists.categoryIds.includes(c.id)).slice(0, QUICK_CATEGORIES);
        return [...selected, ...rest];
    }, [refs.categories, lists.categoryIds]);

    const notWornThreshold = stats?.notWornDaysThreshold ?? 90;
    const countLabel = search
        ? `Найдено ${pluralItems(totalElements)} по запросу «${search}»`
        : pluralItems(totalElements);

    const sortLabel = SORT_OPTIONS.find(o => o.sortBy === sortBy)?.label ?? SORT_OPTIONS[0].label;
    const sortDirLabel = sortDir === 'desc' ? 'По убыванию' : 'По возрастанию';

    const toggleSidebar = () => {
        setSidebarCollapsed(prev => {
            const next = !prev;
            try { localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next)); } catch (_) { /* ignore */ }
            return next;
        });
    };

    const filtersPanel = (
        <CatalogFilters
            refs={refs}
            lists={lists}
            onToggle={toggleListFilter}
            hasDefects={hasDefects}
            waterProof={waterProof}
            favorite={favorite}
            onToggleDefects={() => { setHasDefects(!hasDefects); resetPagination(); }}
            onToggleWaterProof={() => { setWaterProof(waterProof === true ? null : true); resetPagination(); }}
            onToggleFavorite={() => { setFavorite(!favorite); resetPagination(); }}
        />
    );

    const viewToggle = (extraClass: string) => (
        <div data-testid="catalog-view-toggle" className={`${extraClass} rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden`}>
            <button type="button" onClick={() => setViewMode('cards')} title="Карточки" aria-label="Карточки"
                aria-pressed={viewMode === 'cards'}
                className={`p-2 ${viewMode === 'cards' ? 'bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-300' : 'bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                <GridIcon className="w-5 h-5"/>
            </button>
            <button type="button" onClick={() => setViewMode('table')} title="Таблица" aria-label="Таблица"
                aria-pressed={viewMode === 'table'}
                className={`p-2 ${viewMode === 'table' ? 'bg-primary-100 dark:bg-primary-900 text-primary-600 dark:text-primary-300' : 'bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}>
                <TableIcon className="w-5 h-5"/>
            </button>
        </div>
    );

    // ── Render ───────────────────────────────────────────────────────────────

    return (
        // Место под липкую панель действий — только когда она есть, иначе внизу
        // каталога зияет пустая полоса.
        <div className={`space-y-4 ${selectionMode && canBulk ? 'pb-28' : ''}`}>

            {/* ── Top bar ──
                Мобильная адаптация (C12): на xs поиск занимает всю ширину, второй строкой —
                «Добавить» + «Сортировка» + «Фильтры» одной высоты 44 px.
                На узком десктопе (1200 px с сайдбаром) кнопки тулбара занимают почти всю ширину,
                поэтому поиску задана минимальная ширина, а ряд умеет переноситься: иначе поле
                сжималось до иконки (регрессия после выката раунда 3). */}
            <div data-testid="catalog-toolbar" className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                <div data-testid="catalog-search" className="w-full sm:flex-1 sm:basis-64 sm:min-w-[16rem] relative">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"/>
                    <input
                        type="search"
                        value={searchInput}
                        onChange={e => { setSearchInput(e.target.value); resetPagination(); }}
                        placeholder="Поиск по названию, бренду, цвету..."
                        className="w-full pl-9 pr-10 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-ink-800 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                    />
                    {searchInput ? (
                        <IconButton
                            label="Очистить поиск"
                            size="compact"
                            onClick={() => { setSearchInput(''); setSearch(''); }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                            <XIcon className="w-4 h-4"/>
                        </IconButton>
                    ) : (
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-gray-400 dark:text-gray-600 font-mono border border-gray-200 dark:border-gray-700 rounded px-1 select-none">/</span>
                    )}
                </div>

                <div data-testid="catalog-actions" className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
                {/* Add button */}
                <div className="relative flex-1 sm:flex-none min-w-0">
                    <div className="flex sm:inline-flex rounded-xl overflow-hidden shadow-card">
                        <button onClick={() => { setAddMenuOpen(false); setModalOpen(true); }} disabled={!canAdd} className="btn-primary rounded-none gap-2 text-sm flex-1 sm:flex-none min-h-[44px]">
                            <PlusIcon className="w-4 h-4"/> Добавить вещь
                        </button>
                        <button onClick={() => setAddMenuOpen(!addMenuOpen)} aria-label="Другие способы добавления"
                            aria-haspopup="menu" aria-expanded={addMenuOpen}
                            className="btn-primary rounded-none px-2 min-w-[44px] min-h-[44px] border-l border-white/15 dark:border-ink-900/10">
                            <ChevronDownIcon className={`w-4 h-4 transition-transform ${addMenuOpen ? 'rotate-180' : ''}`}/>
                        </button>
                    </div>
                    {addMenuOpen && (
                        <>
                            <div className="fixed inset-0 z-10" onClick={() => setAddMenuOpen(false)}/>
                            <div role="menu" aria-label="Другие способы добавления" data-testid="add-menu"
                                className="fw-popover origin-top-right absolute right-0 mt-1 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-20 min-w-[220px]">
                                <button role="menuitem" type="button" onClick={() => { setAddMenuOpen(false); setModalOpen(true); }} disabled={!isOwnWardrobe}
                                    className="w-full px-4 py-2 min-h-[44px] text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50">
                                    <PlusIcon className="w-4 h-4"/> Заполнить вручную
                                </button>
                                <button role="menuitem" type="button" onClick={() => { setAddMenuOpen(false); setAiModalOpen(true); }} disabled={!canAdd}
                                    className="w-full px-4 py-2 min-h-[44px] text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 disabled:opacity-50">
                                    <SparklesIcon className="w-4 h-4"/> Распознать по фото
                                </button>
                            </div>
                        </>
                    )}
                </div>

                {/* Сортировка: на xs — шторка (в ряду из трёх контролов селект не помещается) */}
                <button
                    type="button"
                    data-testid="mobile-sort-button"
                    title={`Сортировка: ${sortLabel}`}
                    onClick={() => setMobileSortOpen(true)}
                    className="btn-secondary sm:hidden shrink-0 min-h-[44px] px-3 gap-1.5">
                    <ListIcon className="w-4 h-4"/> Сортировка
                </button>

                <div data-testid="catalog-sort" className="hidden sm:flex shrink-0 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-ink-800">
                    <select
                        value={sortBy}
                        aria-label="Сортировка"
                        onChange={e => applySort(e.target.value, sortDir)}
                        className="py-2.5 pl-3 pr-8 min-h-[44px] bg-transparent text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer border-none"
                    >
                        {SORT_OPTIONS.map(o => (
                            <option key={o.sortBy} value={o.sortBy}>{o.label}</option>
                        ))}
                    </select>
                    {/* Направление — кнопка с подписью: одинокий шеврон не читался (C11) */}
                    <button
                        type="button"
                        data-testid="sort-direction"
                        aria-label={sortDirLabel}
                        title={sortDirLabel}
                        onClick={() => applySort(sortBy, sortDir === 'desc' ? 'asc' : 'desc')}
                        className="flex items-center gap-1 px-2.5 min-w-[44px] min-h-[44px] border-l border-gray-200 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-gray-50 dark:hover:bg-ink-700 transition-colors"
                    >
                        {sortDir === 'desc' ? <ChevronDownIcon className="w-4 h-4"/> : <ChevronUpIcon className="w-4 h-4"/>}
                        <span className="hidden xl:inline whitespace-nowrap">{sortDirLabel.toLowerCase()}</span>
                    </button>
                </div>

                {/* Фильтры: на < lg открывают шторку, на lg+ сворачивают/разворачивают сайдбар */}
                <button
                    type="button"
                    data-testid="mobile-filters-button"
                    onClick={() => setMobileFiltersOpen(true)}
                    className="btn-secondary lg:hidden shrink-0 min-h-[44px] px-3 gap-1.5">
                    <FilterIcon className="w-4 h-4"/>
                    Фильтры
                    {activeFilterCount > 0 && (
                        <span data-testid="mobile-filters-badge"
                            className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary-500 text-white text-xs font-bold leading-none">
                            {activeFilterCount}
                        </span>
                    )}
                </button>

                <button
                    type="button"
                    data-testid="desktop-filters-toggle"
                    aria-expanded={!sidebarCollapsed}
                    onClick={toggleSidebar}
                    className="btn-secondary hidden lg:inline-flex shrink-0 min-h-[44px] px-3 gap-1.5">
                    <FilterIcon className="w-4 h-4"/>
                    {activeFilterCount > 0 ? `Фильтры (${activeFilterCount})` : 'Фильтры'}
                </button>

                {canBulk && (
                    <button
                        type="button"
                        data-testid="selection-mode-toggle"
                        aria-pressed={selectionMode}
                        onClick={() => selectionMode ? exitSelection() : setSelectionMode(true)}
                        className="btn-secondary hidden sm:inline-flex shrink-0 min-h-[44px] px-3 gap-1.5">
                        <CheckCheckIcon className="w-4 h-4"/>
                        {selectionMode ? 'Готово' : 'Выбрать'}
                    </button>
                )}

                {viewToggle('hidden lg:flex')}
                </div>{/* end catalog-actions */}
            </div>

            {/* ── Сколько вещей и чем отобраны (C5: вместо ряда KPI-плиток) ── */}
            <div data-testid="catalog-summary" className="flex flex-wrap items-center gap-x-3 gap-y-2">
                <span data-testid="catalog-count" className="text-sm font-medium text-gray-700 dark:text-gray-300 tabular-nums">
                    {countLabel}
                </span>
                {activeChips.length > 0 && (
                    <div data-testid="catalog-active-filters" className="flex items-center gap-2 flex-wrap">
                        {activeChips.map(chip => (
                            // Мобильная адаптация (FR-2): тач-зона чипа ≥ 44px на < lg, компактно на десктопе
                            <span key={chip.key} data-testid={`catalog-active-filter-${chip.key}`}
                                className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 min-h-[44px] lg:min-h-0 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                {chip.label}
                                <button onClick={chip.remove} aria-label={`Сбросить фильтр ${chip.label}`}
                                    data-testid={`catalog-active-filter-remove-${chip.key}`}
                                    className="w-6 h-6 lg:w-4 lg:h-4 rounded-full flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                                    <XIcon className="w-3 h-3"/>
                                </button>
                            </span>
                        ))}
                        <button type="button" onClick={resetFilters} data-testid="catalog-reset-filters"
                            className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:underline">
                            Сбросить все
                        </button>
                    </div>
                )}
            </div>

            {/* ── Быстрые чипы категорий и сигнал «давно не носили» ── */}
            {(quickCategories.length > 0 || (!statsLoading && stats?.notWornRecently != null)) && (
                <div data-testid="catalog-quick-chips" className="flex items-center gap-2 flex-wrap">
                    {quickCategories.map(c => {
                        const active = lists.categoryIds.includes(c.id);
                        return (
                            <button key={c.id} type="button" onClick={() => toggleListFilter('categoryIds', c.id)}
                                aria-pressed={active}
                                className={`inline-flex items-center gap-1 px-3 min-h-[44px] lg:min-h-[32px] rounded-full text-xs font-medium border pressable transition-colors ${
                                    active
                                        ? 'bg-primary-500 text-white border-primary-500'
                                        : 'bg-white dark:bg-ink-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-primary-400 dark:hover:border-primary-600'
                                }`}>
                                {c.nameRu}
                                {c.count != null && <span className="tabular-nums opacity-70">{c.count}</span>}
                            </button>
                        );
                    })}
                    {/* R8: чип ведёт в тот же список, по которому посчитан; при недостатке истории его нет */}
                    {!statsLoading && stats?.notWornRecently != null && (
                        <button type="button"
                            data-testid="stats-not-worn-card"
                            aria-pressed={notWornDays !== null}
                            onClick={() => { setNotWornDays(notWornDays === null ? notWornThreshold : null); resetPagination(); }}
                            className={`inline-flex items-center gap-1.5 px-3 min-h-[44px] lg:min-h-[32px] rounded-full text-xs font-medium border pressable transition-colors ${
                                notWornDays !== null
                                    ? 'bg-amber-500 text-white border-amber-500'
                                    : 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300'
                            }`}>
                            <ClockIcon className="w-3.5 h-3.5"/>
                            Давно не носили ({stats.notWornRecently})
                        </button>
                    )}
                </div>
            )}

            {/* ── Main: content + sidebar ── */}
            <div className="flex gap-5 items-start">

                {/* Content */}
                <div className="flex-1 min-w-0">
                    {loading ? (
                        /* FR-7: скелетоны вместо схлопывания в спиннер при первичной загрузке/смене фильтров */
                        viewMode === 'cards' ? (
                            <div className={GRID_CLASS}>
                                {Array.from({length: 8}, (_, i) => <ItemCardSkeleton key={i}/>)}
                            </div>
                        ) : (
                            <div className="card overflow-x-auto max-w-full">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                                    {Array.from({length: 8}, (_, i) => <TableRowSkeleton key={i}/>)}
                                    </tbody>
                                </table>
                            </div>
                        )
                    ) : loadError ? (
                        /* Error-state (styleguide: error ≠ empty) — текст + «Повторить» внутри data region */
                        <div data-testid="items-error" className="card p-16 text-center">
                            <AlertTriangleIcon className="w-10 h-10 mx-auto text-red-500 dark:text-red-400"/>
                            <p className="mt-4 font-semibold text-gray-900 dark:text-gray-100">Не удалось загрузить гардероб</p>
                            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Проверьте соединение и попробуйте ещё раз.</p>
                            <button onClick={refreshItems} className="btn-secondary mt-4 min-h-[44px] gap-2">
                                <RefreshIcon className="w-4 h-4"/> Повторить
                            </button>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="card p-16 text-center">
                            <p className="text-gray-500 dark:text-gray-400">
                                {hasActiveFilters ? 'По вашему запросу ничего не найдено.' : 'Гардероб пуст. Добавьте первую вещь!'}
                            </p>
                            {hasActiveFilters && (
                                <button onClick={resetFilters} className="btn-secondary mt-4">Сбросить фильтры</button>
                            )}
                        </div>
                    ) : viewMode === 'cards' ? (
                        <>
                            <div data-testid="catalog-grid" className={GRID_CLASS}>
                                {items.map(item => (
                                    <ClothingItemCard
                                        key={item.id}
                                        item={item}
                                        selectionMode={selectionMode}
                                        selected={selectedIds.has(item.id)}
                                        selectable={canBulk && !item.thrownAway}
                                        menuOpen={cardMenuId === item.id}
                                        canEdit={canEdit}
                                        canManageListItems={canManageListItems}
                                        canMoveToTrash={canMoveToTrash}
                                        onToggleSelect={toggleSelected}
                                        onLongPress={handleLongPress}
                                        onOpen={handleItemClick}
                                        onAuxOpen={handleAuxClick}
                                        onToggleFavorite={handleToggleFavorite}
                                        onToggleMenu={handleToggleCardMenu}
                                        onCloseMenu={closeCardMenu}
                                        onNavigate={handleCardNavigate}
                                        onWear={handleCardWear}
                                        onWash={handleCardWash}
                                        onAddToList={handleCardAddToList}
                                        onTrash={handleCardTrash}
                                        onBrandClick={handleBrandFilter}
                                    />
                                ))}
                                {loadingMore && Array.from({length: 4}, (_, i) => <ItemCardSkeleton key={`skeleton-${i}`}/>)}
                            </div>

                            {hasMore && (
                                <div className="flex justify-center pt-6">
                                    <button onClick={loadMore} disabled={loadingMore}
                                        className="btn-secondary px-10 py-2.5 min-w-[220px] flex items-center justify-center gap-2 disabled:opacity-70">
                                        {loadingMore ? <><Spinner/> Загрузка...</> : 'Показать ещё'}
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {/* C8: таблица скроллится в своём контейнере и не тянет страницу вбок */}
                            <div data-testid="catalog-table-scroll" className="card overflow-x-auto max-w-full">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                                    <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        {selectionMode && canBulk && (
                                            <th scope="col" className="px-4 py-3 w-12">
                                                {selectableItems.length > 0 && (
                                                    <input type="checkbox" aria-label="Выбрать всё"
                                                        checked={allSelectableSelected} onChange={toggleSelectAll}
                                                        className="rounded w-4 h-4 cursor-pointer"/>
                                                )}
                                            </th>
                                        )}
                                        {TABLE_COLUMNS.map(col => {
                                            const active = col.sortBy === sortBy;
                                            return (
                                                <th key={col.label} scope="col"
                                                    aria-sort={col.sortBy ? (active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none') : undefined}
                                                    className="px-4 py-1 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">
                                                    {col.sortBy ? (
                                                        <button type="button"
                                                            onClick={() => applySort(col.sortBy as string)}
                                                            className={`inline-flex items-center gap-1 min-h-[44px] uppercase tracking-wider transition-colors ${
                                                                active ? 'text-gray-900 dark:text-gray-100' : 'hover:text-gray-700 dark:hover:text-gray-200'
                                                            }`}>
                                                            {col.label}
                                                            {active && (sortDir === 'asc'
                                                                ? <ChevronUpIcon className="w-3.5 h-3.5"/>
                                                                : <ChevronDownIcon className="w-3.5 h-3.5"/>)}
                                                        </button>
                                                    ) : (
                                                        <span className="inline-flex items-center min-h-[44px]">{col.label}</span>
                                                    )}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                    </thead>
                                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                                    {items.map(item => (
                                        <tr key={item.id}
                                            onClick={e => {
                                                // В режиме выделения строка, как и карточка, отмечает вещь
                                                if (selectionMode && canBulk) {
                                                    if (!item.thrownAway) toggleSelected(item.id);
                                                    return;
                                                }
                                                if (e.ctrlKey || e.metaKey) { window.open(`/clothing/${item.id}`, '_blank'); return; }
                                                navigate(`/clothing/${item.id}`);
                                            }}
                                            onAuxClick={e => { if (e.button === 1) window.open(`/clothing/${item.id}`, '_blank'); }}
                                            className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer ${item.thrownAway ? 'opacity-50' : ''}`}>
                                            {selectionMode && canBulk && (
                                                <td className="px-4 py-2" onClick={e => e.stopPropagation()}>
                                                    {!item.thrownAway && (
                                                        <input type="checkbox" checked={selectedIds.has(item.id)}
                                                            aria-label={`Выбрать: ${item.name}`}
                                                            onChange={() => toggleSelected(item.id)}
                                                            className="rounded w-4 h-4 cursor-pointer"/>
                                                    )}
                                                </td>
                                            )}
                                            <td className="px-4 py-2">
                                                <div data-testid="table-thumb"
                                                    className="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 dark:bg-ink-700 flex items-center justify-center shrink-0">
                                                    {item.firstPhotoUrl ? (
                                                        <AuthImage imageKey={item.firstPhotoUrl} alt={item.name} variant="thumbnail"
                                                            className="w-full h-full object-cover"/>
                                                    ) : (
                                                        <ShirtIcon className="w-5 h-5 text-gray-300 dark:text-gray-600"/>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                                                <span title={item.name}
                                                    className={`block max-w-[16rem] truncate ${item.thrownAway ? 'line-through' : ''}`}>
                                                    {item.name}
                                                </span>
                                                {item.inTrashBin && !item.thrownAway && (
                                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-600/80 text-gray-200 text-xs">
                                                        <TrashIcon className="w-3 h-3"/> Ждёт передачи
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{item.categoryName || '—'}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{item.brandName || '—'}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{item.sizeName || '—'}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 tabular-nums whitespace-nowrap">
                                                {item.totalWears ? pluralTimes(item.totalWears) : '—'}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatDate(item.lastWornAt ?? null)}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 tabular-nums whitespace-nowrap">
                                                {item.aiRatingOverall != null ? `${item.aiRatingOverall.toFixed(1)}/10` : '—'}
                                            </td>
                                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{item.conditionName || '—'}</td>
                                            <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatDate(item.purchaseDate)}</td>
                                        </tr>
                                    ))}
                                    {loadingMore && Array.from({length: 4}, (_, i) => <TableRowSkeleton key={`skeleton-${i}`}/>)}
                                    </tbody>
                                </table>
                            </div>
                            {hasMore && (
                                <div className="flex justify-center pt-6">
                                    <button onClick={loadMore} disabled={loadingMore}
                                        className="btn-secondary px-10 py-2.5 min-w-[220px] flex items-center justify-center gap-2 disabled:opacity-70">
                                        {loadingMore ? <><Spinner/> Загрузка...</> : 'Показать ещё'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {/* FR-5: общий sentinel автоподгрузки — ниже контента (сетка и таблица) */}
                    {!loading && items.length > 0 && (
                        <div ref={sentinelRef} data-testid="infinite-scroll-sentinel" aria-hidden="true" className="h-px"/>
                    )}

                    {/* Единый переключатель размера страницы (CTHS-7). Каталог сохраняет свой
                        исторический набор вариантов и ключ хранения: у пользователей уже есть
                        сохранённый выбор, и смена набора молча сбросила бы его. */}
                    <PageSizeSelect
                        table="catalog"
                        label="Вещей на странице"
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        options={PAGE_SIZE_OPTIONS}
                        totalElements={totalElements}
                        className="justify-end pt-4"
                    />
                </div>

                {/* ── Right column: filters ── */}
                {!sidebarCollapsed && (
                    <aside data-testid="catalog-sidebar" aria-label="Фильтры"
                        className="w-60 xl:w-64 shrink-0 hidden lg:block sticky top-4 self-start">
                        <div className="card p-4">
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Фильтры</span>
                                <div className="flex items-center gap-2">
                                    {hasActiveFilters && (
                                        <button onClick={resetFilters} className="text-xs text-primary-600 dark:text-primary-400 hover:underline">Сбросить</button>
                                    )}
                                    <IconButton label="Свернуть фильтры" size="compact" onClick={toggleSidebar}
                                        className="rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                                        <XIcon className="w-4 h-4"/>
                                    </IconButton>
                                </div>
                            </div>

                            {filtersPanel}
                        </div>
                    </aside>
                )}
            </div>

            {/* ── Липкая панель массовых действий (C6) ── */}
            {selectionMode && canBulk && (
                <div data-testid="selection-bar"
                    className="fw-sticky-bar fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 dark:border-white/10 bg-white/95 dark:bg-ink-800/95 backdrop-blur px-4 py-3"
                    style={{paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))'}}>
                    <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200 tabular-nums">
                            Выбрано: {selectedCount}
                        </span>
                        {!allSelectableSelected && selectableItems.length > 0 && (
                            <button onClick={toggleSelectAll}
                                className="text-xs text-primary-600 dark:text-primary-400 hover:underline min-h-[44px] px-1">
                                Выбрать всё
                            </button>
                        )}
                        <div className="flex-1"/>
                        {canMoveToTrash && (
                            <button onClick={handleBulkMoveToTrash} disabled={selectedCount === 0}
                                className="btn-secondary min-h-[44px] gap-1.5 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50">
                                <TrashIcon className="w-4 h-4"/> Решить расстаться
                            </button>
                        )}
                        {canManageListItems && (
                            <button onClick={() => setBulkAddModalOpen(true)} disabled={selectedCount === 0}
                                className="btn-primary min-h-[44px] gap-1.5 disabled:opacity-50">
                                <ListIcon className="w-4 h-4"/> В список
                            </button>
                        )}
                        <button onClick={exitSelection}
                            className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 min-h-[44px] px-2">
                            Снять выделение
                        </button>
                    </div>
                </div>
            )}

            {/* ── Мобильный bottom sheet фильтров (styleguide: drawer z ниже modal z-50) ── */}
            {mobileFiltersOpen && (
                <div className="fw-sheet fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Фильтры"
                    data-testid="mobile-filters-drawer">
                    <div className="absolute inset-0 bg-black/50" data-testid="mobile-filters-overlay" aria-hidden
                        onClick={() => setMobileFiltersOpen(false)}/>
                    <div className="absolute inset-x-0 bottom-0 max-h-[85vh] flex flex-col rounded-t-3xl bg-white dark:bg-ink-800 shadow-card">
                        <div className="flex items-center justify-between pl-4 pr-2 py-2 border-b border-gray-100 dark:border-gray-700 shrink-0">
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Фильтры</span>
                            <div className="flex items-center gap-1">
                                {viewToggle('flex')}
                                {hasActiveFilters && (
                                    <button onClick={resetFilters}
                                        className="px-3 min-h-[44px] text-xs text-primary-600 dark:text-primary-400 hover:underline">
                                        Сбросить
                                    </button>
                                )}
                                <button
                                    type="button"
                                    aria-label="Закрыть фильтры"
                                    onClick={() => setMobileFiltersOpen(false)}
                                    className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                    <XIcon className="w-5 h-5"/>
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto px-4 pb-2">
                            {filtersPanel}
                        </div>
                        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 shrink-0">
                            <button onClick={() => setMobileFiltersOpen(false)} className="btn-primary w-full min-h-[44px]">
                                Показать {pluralItems(totalElements)}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Мобильная шторка сортировки (C12) ── */}
            <Dialog
                open={mobileSortOpen}
                onClose={() => setMobileSortOpen(false)}
                title="Сортировка"
                data-testid="mobile-sort-sheet"
                overlayClassName="fw-sheet fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:hidden"
                panelClassName="bg-white dark:bg-ink-800 rounded-t-3xl shadow-xl w-full max-h-[85vh] flex flex-col outline-none"
                bodyClassName="p-2 overflow-y-auto"
            >
                <div role="group" aria-label="Порядок сортировки" className="flex gap-2 px-2 pb-2">
                    {(['desc', 'asc'] as const).map(dir => (
                        <button key={dir} type="button"
                            aria-pressed={sortDir === dir}
                            onClick={() => applySort(sortBy, dir)}
                            className={`flex-1 inline-flex items-center justify-center gap-1.5 min-h-[44px] rounded-xl border text-sm ${
                                sortDir === dir
                                    ? 'bg-primary-500 text-white border-primary-500'
                                    : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                            }`}>
                            {dir === 'desc' ? <ChevronDownIcon className="w-4 h-4"/> : <ChevronUpIcon className="w-4 h-4"/>}
                            {dir === 'desc' ? 'По убыванию' : 'По возрастанию'}
                        </button>
                    ))}
                </div>
                <ul className="pb-2">
                    {SORT_OPTIONS.map(o => (
                        <li key={o.sortBy}>
                            <button type="button"
                                aria-pressed={sortBy === o.sortBy}
                                onClick={() => { applySort(o.sortBy, sortDir); setMobileSortOpen(false); }}
                                className={`w-full flex items-center justify-between gap-2 px-4 min-h-[44px] text-left text-sm rounded-xl ${
                                    sortBy === o.sortBy
                                        ? 'text-primary-600 dark:text-primary-400 font-medium'
                                        : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-white/5'
                                }`}>
                                {o.label}
                                {sortBy === o.sortBy && <CheckCheckIcon className="w-4 h-4"/>}
                            </button>
                        </li>
                    ))}
                </ul>
            </Dialog>

            {/* ── Modals ── */}
            <AddClothingItemModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onSuccess={refreshAll}
                onCreated={maybePromptOneInOneOut}
                onRecognizeByPhoto={() => { setModalOpen(false); setAiModalOpen(true); }}/>
            {oneInOneOutItem && (
                <OneInOneOutDialog
                    addedItemId={oneInOneOutItem.id}
                    addedItemName={oneInOneOutItem.name}
                    ownerId={selectedOwnerId}
                    onClose={() => setOneInOneOutItem(null)}
                    onDisposed={refreshAll}
                />
            )}
            <AddAiRecognitionModal isOpen={aiModalOpen} onClose={() => setAiModalOpen(false)} onSuccess={() => { setAiModalOpen(false); navigate('/ai-recognition'); }}/>
            <BulkAddToListsModal isOpen={bulkAddModalOpen} onClose={() => setBulkAddModalOpen(false)} itemIds={Array.from(selectedIds)} ownerId={selectedOwnerId}
                onSuccess={() => { setBulkAddModalOpen(false); exitSelection(); refreshAll(); }}/>
        </div>
    );
};

export default ClothingList;
